import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

/* ---------------- Toasts (04 §5.9, §8.1: polite except danger; 8 s undo window) ---------------- */

export type ToastTone = 'info' | 'ok' | 'warn' | 'danger';

export interface ToastSpec {
  tone?: ToastTone;
  message: ReactNode;
  action?: { label: ReactNode; onPress: () => void };
  /** ms; default 8000 (the undo window). */
  timeout?: number;
}

export interface ToastEntry extends ToastSpec {
  id: number;
}

export interface ToastApi {
  show(toast: ToastSpec): number;
  dismiss(id: number): void;
}

export const ToastContext = createContext<{ api: ToastApi; toasts: ToastEntry[] } | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const next = useRef(1);
  const dismiss = useCallback((id: number) => setToasts((all) => all.filter((t) => t.id !== id)), []);
  const show = useCallback(
    (toast: ToastSpec) => {
      const id = next.current++;
      setToasts((all) => [...all.slice(-2), { ...toast, id }]);
      setTimeout(() => dismiss(id), toast.timeout ?? 8000);
      return id;
    },
    [dismiss],
  );
  const value = useMemo(() => ({ api: { show, dismiss }, toasts }), [show, dismiss, toasts]);
  return <ToastContext value={value}>{children}</ToastContext>;
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast() outside <ToastProvider>');
  return ctx.api;
}

